import React from 'react';
import { ImagePlus, Trash2, Loader2 } from 'lucide-react';
import { Field, Input, Select, UnitInput, Switch, Segmented, Checkbox } from '../ui/Field';
import { moduleApi } from '../../lib/api';
import { type PrestationSubcategory } from './SimulateurObjets';
import {
    type PrestationCatalogItem,
    type PrestationCategory,
    type UnitType,
    type ClientTypeScope,
    UNIT_TYPE_LABELS,
    UNIT_LABEL_COURT,
    type OptionOverride,
} from './prestationsTypes';

/**
 * Formulaire d'une prestation (maquette 10b « la carte dépliée — édition en place, pas de modale »).
 * Trois sections : Nature & rattachement · Tarif · Simulateur public. Aucune règle métier ne change
 * ici : mêmes champs, mêmes valeurs, mêmes appels à onChange qu'avant la refonte.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="space-y-3">
        <h4 className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">{title}</h4>
        {children}
    </section>
);


// Photo de la prestation (2026-09-23, demande d'Emeni pour N.Signature) : facultative, et l'établissement
// choisit où elle s'affiche. L'image ne voyage jamais dans le catalogue — elle a son propre appel, et elle
// est réduite ici, dans le navigateur : une photo de téléphone fait 3 à 8 Mo, ce que la base refuserait.
const LARGEUR_MAX = 1000;

const reduireImage = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Fichier illisible'));
    reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Image illisible'));
        img.onload = () => {
            const ratio = Math.min(1, LARGEUR_MAX / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * ratio);
            canvas.height = Math.round(img.height * ratio);
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('Rendu impossible')); return; }
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        img.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
});

function PhotoPrestation({ item, onChange }: { item: PrestationCatalogItem; onChange: (patch: Partial<PrestationCatalogItem>) => void }) {
    const [image, setImage] = React.useState<string | null>(null);
    const [chargement, setChargement] = React.useState(true);
    const [envoi, setEnvoi] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        let annule = false;
        setChargement(true);
        moduleApi.getPrestationImage(item.id)
            .then((r: any) => { if (!annule) setImage(r?.image || null); })
            .catch(() => { if (!annule) setImage(null); })
            .finally(() => { if (!annule) setChargement(false); });
        return () => { annule = true; };
    }, [item.id]);

    const choisir = async (file?: File) => {
        if (!file) return;
        setErreur('');
        setEnvoi(true);
        try {
            const dataUrl = await reduireImage(file);
            await moduleApi.savePrestationImage(item.id, dataUrl);
            setImage(dataUrl);
            // Une photo sans case cochée ne s'afficherait nulle part : on active les deux à la première
            // photo déposée, l'établissement décoche ensuite ce qu'il ne veut pas.
            if (!item.image_show_demarche && !item.image_show_simulateur) {
                onChange({ image_show_demarche: true, image_show_simulateur: true });
            }
        } catch (e: any) {
            setErreur(e?.message || "La photo n'a pas pu être enregistrée.");
        } finally {
            setEnvoi(false);
        }
    };

    const supprimer = async () => {
        setEnvoi(true);
        try {
            await moduleApi.deletePrestationImage(item.id);
            setImage(null);
        } catch (e: any) {
            setErreur(e?.message || "La photo n'a pas pu être retirée.");
        } finally {
            setEnvoi(false);
        }
    };

    return (
        <Section title="Photo">
            <p className="text-[11px] text-content-muted">Facultative. Elle s'enregistre tout de suite.</p>
            <div className="flex items-start gap-3 flex-wrap">
                <div className="w-28 h-28 shrink-0 rounded-xl border border-border bg-surface-soft overflow-hidden flex items-center justify-center">
                    {chargement ? <Loader2 size={16} className="animate-spin text-content-muted" />
                        : image ? <img src={image} alt={item.label || 'Photo de la prestation'} className="w-full h-full object-cover" />
                        : <ImagePlus size={20} className="text-content-muted" />}
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <input ref={inputRef} type="file" accept="image/*" className="hidden"
                            onChange={(e) => { choisir(e.target.files?.[0]); e.target.value = ''; }} />
                        <button type="button" onClick={() => inputRef.current?.click()} disabled={envoi}
                            className="px-3 py-2 rounded-xl border border-border text-xs font-bold text-content hover:border-brand-500 disabled:opacity-50">
                            {envoi ? 'Envoi…' : image ? 'Remplacer la photo' : 'Ajouter une photo'}
                        </button>
                        {image && (
                            <button type="button" onClick={supprimer} disabled={envoi} title="Retirer la photo"
                                className="p-2 rounded-lg text-content-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                                <Trash2 size={15} />
                            </button>
                        )}
                    </div>
                    <Switch label="Afficher dans la démarche client"
                        checked={item.image_show_demarche === true}
                        onChange={(e) => onChange({ image_show_demarche: e.target.checked })} />
                    <Switch label="Afficher dans le simulateur de devis"
                        checked={item.image_show_simulateur === true}
                        onChange={(e) => onChange({ image_show_simulateur: e.target.checked })} />
                </div>
            </div>
            {erreur && <p className="text-[11px] text-red-400 font-semibold">{erreur}</p>}
        </Section>
    );
}

// Où une option s'applique et son prix selon la prestation ne se règlent plus ici : tout est dans « Réductions et options »
// (une seule configuration). Le catalogue garde le nom, le prix de base, l'unité et le temps de l'option.
function CiblesOption() {
    return (
        <div>
            <span className="field-label">S'applique à</span>
            <span className="field-help">Se règle dans « Réductions et options » : l'option est proposée sur chaque prestation où vous saisissez un prix ou un pourcentage, au prix indiqué là-bas.</span>
        </div>
    );
}

export function PrestationForm({
    item,
    allowedTvaRates,
    categories,
    subcategories,
    catalog,
    onChange,
    hideAudience,
    prixEnPourcent,
}: {
    item: PrestationCatalogItem;
    allowedTvaRates: number[];
    categories: PrestationCategory[];
    subcategories: PrestationSubcategory[];
    catalog: PrestationCatalogItem[];
    onChange: (patch: Partial<PrestationCatalogItem>) => void;
    // Masqué à la création : la valeur est déjà fixée par l'onglet actif. Reste visible en édition.
    hideAudience?: boolean;
    // Option dont le prix est un pourcentage du prix de la prestation (réglé dans « Réductions et options ») : le prix du catalogue n'est plus utilisé.
    prixEnPourcent?: boolean;
}) {
    const estOption = item.kind === 'option';
    const [plus, setPlus] = React.useState(false);

    return (
        <div className="space-y-4">
            {/* L'essentiel : ce qu'on règle presque à chaque fois. Tout le reste est replié dans « Plus de réglages ». */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {estOption && prixEnPourcent ? (
                    <div>
                        <span className="field-label">Prix</span>
                        <span className="field-help">Pourcentage du prix de chaque prestation. Il se règle dans « Réductions et options » : le prix saisi ici ne servirait à rien.</span>
                    </div>
                ) : (
                    <Field label="Prix TTC">
                        {({ id }) => (
                            <UnitInput id={id} unit="€" type="number" step="0.01" min={0} value={item.unit_price_ttc}
                                onChange={(e) => onChange({ unit_price_ttc: Math.max(0, Number(e.target.value || 0)) })} />
                        )}
                    </Field>
                )}
                <Field label="Vendu au">
                    {({ id }) => (
                        <Select id={id} value={item.unit_type} onChange={(e) => onChange({ unit_type: e.target.value as UnitType })}>
                            {(Object.keys(UNIT_TYPE_LABELS) as UnitType[]).map((key) => (
                                <option key={key} value={key}>{UNIT_TYPE_LABELS[key]}</option>
                            ))}
                        </Select>
                    )}
                </Field>
                <Field label={`Temps par ${UNIT_LABEL_COURT[item.unit_type]}`}>
                    {({ id }) => (
                        <UnitInput id={id} unit="min" type="number" step="5" min={0} value={item.default_duration_minutes}
                            onChange={(e) => onChange({ default_duration_minutes: Math.max(0, Number(e.target.value || 0)) })} />
                    )}
                </Field>
            </div>
            <p className="field-help">Le temps sert à calculer la durée d'un rendez-vous dans le Planning.</p>

            {item.unit_type === 'surface' && (
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Largeur">
                        {({ id }) => <Input id={id} type="number" step="1" min={0} value={item.surface_width}
                            onChange={(e) => onChange({ surface_width: Math.max(0, Number(e.target.value || 0)) })} />}
                    </Field>
                    <Field label="Longueur">
                        {({ id }) => <Input id={id} type="number" step="1" min={0} value={item.surface_length}
                            onChange={(e) => onChange({ surface_length: Math.max(0, Number(e.target.value || 0)) })} />}
                    </Field>
                </div>
            )}

            {!estOption && subcategories.length > 0 && (
                <Field label="Objet">
                    {({ id }) => (
                        <Select id={id} value={item.subcategory_id || ''} onChange={(e) => onChange({ subcategory_id: e.target.value })}>
                            <option value="">Aucun</option>
                            {subcategories.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                        </Select>
                    )}
                </Field>
            )}

            {estOption && <CiblesOption />}

            <button type="button" onClick={() => setPlus(!plus)} aria-expanded={plus}
                className="text-xs font-bold text-content-muted hover:text-content inline-flex items-center gap-1.5">
                <span aria-hidden="true">{plus ? '−' : '+'}</span> Plus de réglages
            </button>

            {plus && (
                <div className="space-y-5 pt-1 border-t border-border">
                    <Section title="Classement">
                        <div>
                            <span className="field-label">Nature</span>
                            <Segmented
                                ariaLabel="Nature de la ligne"
                                value={estOption ? 'option' : 'prestation'}
                                onChange={(v) => onChange({ kind: v as 'prestation' | 'option' })}
                                options={[{ value: 'prestation', label: 'Prestation' }, { value: 'option', label: 'Option' }]}
                            />
                            <span className="field-help">{estOption ? "Une option se coche en plus d'une prestation (désinfection, hydrofuge…)." : 'Ce que le client fait nettoyer.'}</span>
                        </div>
                        {categories.length > 0 && (
                            <Field label="Catégorie (pour les seuils de formule)">
                                {({ id }) => (
                                    <Select id={id} value={item.category_id || ''} onChange={(e) => onChange({ category_id: e.target.value })}>
                                        <option value="">Aucune</option>
                                        {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                    </Select>
                                )}
                            </Field>
                        )}
                        {!hideAudience && (
                            <div>
                                <span className="field-label">Proposée à</span>
                                <Segmented
                                    ariaLabel="Public visé"
                                    value={item.client_type_scope || 'TOUS'}
                                    onChange={(v) => onChange({ client_type_scope: v as ClientTypeScope })}
                                    options={[{ value: 'TOUS', label: 'Tous' }, { value: 'PROFESSIONNEL', label: 'Pro' }, { value: 'PRIVÉ', label: 'Particulier' }]}
                                />
                            </div>
                        )}
                    </Section>

                    <Section title="Lieu et TVA">
                        <div>
                            <span className="field-label">Lieu d'intervention</span>
                            <Segmented
                                ariaLabel="Lieu d'intervention de cette prestation"
                                value={item.location_mode || ''}
                                onChange={(v) => onChange({ location_mode: v as '' | 'domicile' | 'entreprise' | 'les_deux' })}
                                options={[
                                    { value: '', label: "Comme l'établissement" },
                                    { value: 'entreprise', label: 'Nos locaux' },
                                    { value: 'domicile', label: 'Chez le client' },
                                    { value: 'les_deux', label: 'Les deux' },
                                ]}
                            />
                        </div>
                        <div className="flex items-end gap-4 flex-wrap">
                            <Switch label="Assujetti à la TVA" checked={item.show_tva} onChange={(e) => onChange({ show_tva: e.target.checked })} />
                            {item.show_tva && (
                                <Field label="TVA" className="w-[120px] shrink-0">
                                    {({ id }) => (
                                        <Select id={id} value={item.tax_rate} onChange={(e) => onChange({ tax_rate: Number(e.target.value) })}>
                                            {allowedTvaRates.map((rate) => <option key={rate} value={rate}>{rate} %</option>)}
                                        </Select>
                                    )}
                                </Field>
                            )}
                        </div>
                    </Section>

                    <Section title="Simulateur public">
                        <Field label="Libellé court" help="Vide : le nom de la prestation est utilisé.">
                            {({ id, describedBy }) => (
                                <Input id={id} aria-describedby={describedBy} type="text" value={item.simulateur_label || ''}
                                    placeholder={item.label || '3 places'} onChange={(e) => onChange({ simulateur_label: e.target.value })} />
                            )}
                        </Field>
                        <Switch label="Masquer dans le simulateur public" checked={item.simulateur_hidden === true}
                            onChange={(e) => onChange({ simulateur_hidden: e.target.checked })} />
                    </Section>

                    <PhotoPrestation item={item} onChange={onChange} />
                </div>
            )}
        </div>
    );
}
